import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useUpdateUserPreferences } from '../api/userPreferences';
import { useMySubscriptions, useUpdateMySubscriptions } from '../api/admin';
import SubscriptionsTable from '../components/notifications/SubscriptionsTable';

interface SnackState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

export function SectionCard({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {description}
      </Typography>
      {children}
    </Paper>
  );
}

export default function MyAccountPage() {
  const { user, updateUser } = useAuth();
  const updatePreferences = useUpdateUserPreferences();
  const {
    data: subscriptionData,
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
  } = useMySubscriptions();
  const updateSubscriptions = useUpdateMySubscriptions();
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error') =>
    setSnack({ open: true, message, severity });

  // Email
  const [email, setEmail] = useState('');
  // Phone
  const [phone, setPhone] = useState('');
  const [testingSms, setTestingSms] = useState(false);
  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    setEmail(user?.email ?? '');
    setPhone(user?.phone_number ?? '');
  }, [user?.email, user?.phone_number]);

  // The two halves scroll internally instead of the page: measure where the
  // container starts and claim exactly the remaining viewport height (the
  // admin user-settings page does the same — a fixed 100vh would overhang
  // the app bar).
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState('100vh');
  useLayoutEffect(() => {
    const measure = () => {
      const top = containerRef.current?.getBoundingClientRect().top ?? 0;
      // Screen-pixel maths inside the zoomed UI (see index.css --ui-zoom).
      setContainerHeight(`calc((100vh - ${Math.round(top)}px) / var(--ui-zoom))`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const emailDirty = email !== (user?.email ?? '');
  // Accounts that sign in through single sign-on (or are linked from another
  // organisation) have no password to change, and their email is what the
  // identity provider matches on — so it is shown but not edited here.
  const usesPassword = (user?.auth_source ?? 'password') === 'password';
  const emailManaged = !!user?.sso_linked || user?.auth_source === 'linked';
  const phoneDirty = phone !== (user?.phone_number ?? '');

  const errorMessage = (error: unknown, fallback: string) => {
    const err = error as {
      message?: string;
      fields?: Record<string, string[]>;
    };
    const fieldError = err?.fields && Object.values(err.fields)[0]?.[0];
    if (fieldError) return fieldError;
    if (err?.message && err.message !== 'Request failed') return err.message;
    return fallback;
  };

  const handleSaveEmail = async () => {
    try {
      const result = await updatePreferences.mutateAsync({ email });
      updateUser({ email: result.data.email });
      showSnack('Email updated.', 'success');
    } catch (error) {
      showSnack(errorMessage(error, 'Could not update the email address.'), 'error');
    }
  };

  const handleSavePhone = async () => {
    try {
      const result = await updatePreferences.mutateAsync({ phone_number: phone });
      updateUser({ phone_number: result.data.phone_number });
      setPhone(result.data.phone_number ?? '');
      showSnack(
        result.data.phone_number
          ? `Saved ${result.data.phone_number}.`
          : 'Phone number cleared.',
        'success'
      );
    } catch (error) {
      showSnack(errorMessage(error, 'Could not save the phone number.'), 'error');
    }
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    try {
      const result = await api.post<{ data: { message: string } }>(
        '/api/me/test-sms',
        {}
      );
      showSnack(result.data.message, 'success');
    } catch (error) {
      showSnack(errorMessage(error, 'Test SMS failed.'), 'error');
    } finally {
      setTestingSms(false);
    }
  };

  const passwordReady =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    newPassword === confirmPassword;

  const handleChangePassword = async () => {
    setChangingPassword(true);
    try {
      await api.post('/api/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showSnack('Password updated.', 'success');
    } catch (error) {
      showSnack(errorMessage(error, 'Could not change the password.'), 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    // textAlign resets the Vite template's global `#root { text-align:
    // center }` — without it every heading and helper line centers. Laid out
    // like the admin user-settings page: on desktop the container pins to the
    // viewport and the two halves scroll internally; on small screens it
    // reverts to a normal stacked, page-scrolling layout.
    <Container
      ref={containerRef}
      // xl: wider than a reading column, but still centered with margins.
      maxWidth="xl"
      sx={{
        pt: 3,
        pb: { xs: 3, md: 2 },
        textAlign: 'left',
        height: { md: containerHeight },
        display: 'flex',
        flexDirection: 'column',
        overflow: { md: 'hidden' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexShrink: 0 }}>
        <ManageAccountsIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
        <Typography variant="h5" fontWeight="bold">
          My Account
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexShrink: 0 }}>
        Signed in as <strong>{user?.username}</strong>. These details are yours
        alone — other members of your organisation have their own.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          // The form panels cap out around a comfortable field width; every
          // extra pixel goes to the table, which can actually use it.
          gridTemplateColumns: { xs: '1fr', md: 'minmax(380px, 4fr) 8fr' },
          gap: 3,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left half: account panels in their own scroll area. SectionCard
            brings a bottom margin for stacked layouts; the column gap
            replaces it. */}
        <Box
          sx={{
            minHeight: 0,
            overflowY: { md: 'auto' },
            pr: { md: 0.5 },
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            '& > .MuiPaper-root': { mb: 0, flexShrink: 0 },
          }}
        >
          <SectionCard
            icon={<AlternateEmailIcon fontSize="small" sx={{ color: 'secondary.main' }} />}
            title="Email address"
            description={
              emailManaged
                ? "Where email notifications are sent. Managed by your organisation's sign-in, so it can't be changed here."
                : 'Where email notifications and account communication are sent.'
            }
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                size="small"
                type="email"
                name="email"
                autoComplete="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={emailManaged}
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveEmail}
                disabled={emailManaged || !emailDirty || updatePreferences.isPending}
              >
                Save
              </Button>
          </Box>
        </SectionCard>

        <SectionCard
          icon={<PhoneIphoneIcon fontSize="small" sx={{ color: 'secondary.main' }} />}
          title="Mobile number"
          description="SMS notifications for realtime detections are sent to this number. Use international format, e.g. +61412345678. Leave blank to receive no SMS."
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size="small"
              type="tel"
              name="tel"
              autoComplete="tel"
              label="Mobile number"
              placeholder="+61412345678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSavePhone}
              disabled={!phoneDirty || updatePreferences.isPending}
            >
              Save
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 1 }}>
            <Button
              size="small"
              color="secondary"
              onClick={handleTestSms}
              disabled={testingSms || phoneDirty || !user?.phone_number}
            >
              {testingSms ? 'Sending…' : 'Send test SMS'}
            </Button>
            {phoneDirty && (
              <Typography variant="caption" color="text.secondary">
                Save the number before sending a test.
              </Typography>
            )}
          </Box>
        </SectionCard>

        {usesPassword ? (
        <SectionCard
          icon={<LockOutlinedIcon fontSize="small" sx={{ color: 'secondary.main' }} />}
          title="Password"
          description="Changing your password signs out your other sessions; this one stays signed in."
        >
          {/* A real form with autocomplete hints keeps the browser's password
              manager pointed at these fields — and away from the ones above. */}
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (passwordReady && !changingPassword) handleChangePassword();
            }}
          >
            <TextField
              fullWidth
              size="small"
              type="password"
              autoComplete="current-password"
              label="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              autoComplete="new-password"
              label="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              autoComplete="new-password"
              label="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && newPassword !== confirmPassword
                  ? 'Passwords do not match'
                  : ' '
              }
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={!passwordReady || changingPassword}
              >
                {changingPassword ? 'Changing…' : 'Change password'}
              </Button>
            </Box>
          </Box>
        </SectionCard>
        ) : (
        <SectionCard
          icon={<LockOutlinedIcon fontSize="small" sx={{ color: 'secondary.main' }} />}
          title="Sign-in"
          description={
            user?.auth_source === 'linked'
              ? `You're working in this organisation from your ${user?.home_user?.tenant_name ?? 'own'} account. Manage your sign-in there.`
              : "You sign in through your organisation's single sign-on, so there is no Mitigate password to manage."
          }
        >
          <Box />
        </SectionCard>
        )}
        </Box>

        {/* Right half: the notifications panel. The card itself is fixed to
            the column; only its table body scrolls, under sticky headers. */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexShrink: 0 }}>
            <NotificationsActiveIcon fontSize="small" sx={{ color: 'secondary.main' }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Realtime detection notifications
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexShrink: 0 }}>
            Email or SMS alerts when detections arrive near a project. Every
            project you can see is listed; use "All" to subscribe to every
            detection type for a project at once. Alert radius and snoozing
            live on each project's Notifications tab.
          </Typography>

          <SubscriptionsTable
            data={subscriptionData}
            isLoading={subscriptionsLoading}
            error={subscriptionsError}
            hasPhone={Boolean(user?.phone_number)}
            subject="you"
            onToggle={(projectId, subscriptions, onSettled) =>
              updateSubscriptions.mutate(
                { project_id: projectId, subscriptions },
                {
                  onError: (err) =>
                    showSnack(
                      (err as { message?: string })?.message &&
                        (err as { message?: string }).message !== 'Request failed'
                        ? `Could not update the subscription: ${(err as { message?: string }).message}`
                        : 'Could not update the subscription.',
                      'error'
                    ),
                  onSettled,
                }
              )
            }
            />
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
